import { useEffect, useRef, useState, type ElementRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudTextInput, MudTag } from '@egov-moldova/mud-react';
import { formatMedicineLabel } from '@simsm/shared-types';
import { formatDate, useAnchoredOverlay, useComboboxA11y } from '@simsm/ui-shared';
import {
  GROUPED_SEARCH_LIMIT,
  describeAuthorization,
  groupByUniqueMedicine,
  medicineOrigin,
  searchMedicines,
  todayIso,
  type MedicineHit,
} from './medicine-api';

interface MedicineComboboxProps {
  /** The nomenclator code stored on the row — empty if nothing picked yet. */
  value: string;
  /**
   * The label of the last picked medicine, preserved across re-renders so the
   * field reads as the medicine and not as its code. A string rather than a
   * medicine: `StockReportingPage` restores it from the draft row, which stores
   * only the composed label, and used to hand over a synthetic `MedicineDisplay`
   * with the whole label stuffed into its `dci` — a third copy of the join.
   */
  pickedLabel?: string | undefined;
  invalid?: boolean;
  /**
   * List unique medicines, each with the codes it is published under, instead of one
   * row per code (caiet UC92-02: one medicine; CF-34). A pick is the first code of the
   * medicine, with no brand: the server resolves a code to its unique medicine.
   */
  groupByMedicine?: boolean;
  onPick: (hit: MedicineHit) => void;
  onClear: () => void;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * caiet UC50-02 / UC91-02 / UC92-02 — extended search in the combined
 * nomenclator. The search matches DCI, ATC and the code itself; a pick carries
 * the nomenclator code, which is the only medicine key the stock tables hold.
 */
export function MedicineCombobox({
  value,
  pickedLabel,
  invalid,
  groupByMedicine = false,
  onPick,
  onClear,
}: MedicineComboboxProps): JSX.Element {
  const { t } = useTranslate();
  const displayValue = pickedLabel ?? value;
  const [query, setQuery] = useState(displayValue);
  const [open, setOpen] = useState(false);
  // Dismissal is not the same state as "the field is not in use". Escape hides
  // the suggestions and leaves the query alone; closing (a pick, a click
  // outside) also reverts the text to the picked label, which on an unfinished
  // search would silently erase what the user had typed.
  const [dismissed, setDismissed] = useState(false);
  const debounced = useDebounced(query, 200);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<ElementRef<typeof MudTextInput>>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  // The helper's `open` must be the condition the panel actually renders under:
  // it measures the portalled element, and an element that is not mounted cannot
  // be measured.
  const panelOpen = open && !dismissed && debounced.trim().length >= 2;
  // The panel is portalled to <body>, so placement belongs to the shared
  // anchored-overlay helper and not to this component: it flips above the field
  // when the row sits near the fold, clamps to every viewport edge and
  // repositions while the page scrolls — spec 002 contracts/shared-fixes.md §2.
  // Computing a `position: fixed` offset here again is the defect returning.
  //
  // The height cap stays this component's own. `.simsm-medicombo__panel`'s
  // `max-height: 320px` in global.css is what the panel renders at: the helper
  // reads that rule, places the panel as the box it makes, and adds a cap of its
  // own only when even 320px does not fit beside the trigger. It has to work that
  // way round — an inline `max-height` outranks any class rule, so writing one
  // unconditionally repeals the consumer's, which is how this panel came to render
  // 330px tall, uncapped and unscrollable, under an inline cap of 668px.
  const { style: panelStyle, placement } = useAnchoredOverlay({
    open: panelOpen,
    triggerRef: rootRef,
    overlayRef: panelRef,
    matchTriggerWidth: true,
  });

  useEffect(() => {
    // Don't clobber what the user is actively typing: an autosave round-trip
    // updating the row's value would otherwise reset the query and close the
    // dropdown mid-search. Sync the display label only while the panel is shut.
    if (!open) setQuery(displayValue);
  }, [displayValue, open]);

  useEffect(() => {
    function onClick(e: MouseEvent): void {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onClick);
    return () => document.removeEventListener('pointerdown', onClick);
  }, []);

  const search = useQuery({
    queryKey: ['medicine-search', debounced, groupByMedicine],
    queryFn: () =>
      groupByMedicine
        ? searchMedicines(debounced, GROUPED_SEARCH_LIMIT)
        : searchMedicines(debounced),
    enabled: debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  function handleInput(v: string): void {
    setQuery(v);
    setOpen(true);
    setDismissed(false);
    if (v.length === 0) onClear();
  }

  function handlePick(hit: MedicineHit): void {
    onPick(hit);
    setOpen(false);
  }

  const found = search.data?.items ?? [];
  const groups = groupByMedicine ? groupByUniqueMedicine(found).slice(0, 8) : [];
  const hits = groupByMedicine ? groups.map((group) => group.hit) : found;
  // Read once per render, so every row in one panel judges its expiry against
  // the same day.
  const today = todayIso();

  // Audit E7 / WCAG SC 2.1.1: the field was a bare `mud-text-input` beside a
  // `role="group"` panel of buttons — no combobox role, no arrow keys, no
  // Escape, and the portalled panel sitting after the whole page so the first
  // suggestion was 35 Tab presses away. See `combobox-a11y.ts` for why this is
  // the focus-moves-into-the-listbox variant of the ARIA pattern.
  const combobox = useComboboxA11y({
    open: panelOpen,
    optionCount: hits.length,
    fieldRef,
    listboxRef,
    onSelect: (index) => {
      const hit = hits[index];
      if (hit) handlePick(hit);
    },
    onOpen: () => {
      setOpen(true);
      setDismissed(false);
    },
    onClose: () => setDismissed(true),
    onTypeahead: (character) => handleInput(query + character),
  });

  return (
    <div
      ref={rootRef}
      className="simsm-medicombo"
      // The field's keys are caught here rather than on `mud-text-input`: keydown is
      // composed, so it crosses the shadow boundary and reaches this wrapper
      // whichever element inside the component is focused.
      onKeyDown={combobox.onFieldKeyDown}
    >
      <MudTextInput
        ref={fieldRef}
        size="md"
        value={query}
        placeholder={t('medicine_combobox.placeholder')}
        invalid={invalid === true}
        onMudInput={(e: CustomEvent<{ value: string }>) => handleInput(e.detail.value)}
        onMudFocus={() => {
          if (!combobox.shouldOpenOnFieldFocus()) return;
          setOpen(true);
          setDismissed(false);
        }}
      />
      {panelOpen
        ? createPortal(
            <div
              ref={panelRef}
              className="simsm-medicombo__panel"
              style={panelStyle}
              // Asserted by the e2e geometry check, and a flipped panel may want
              // different chrome later.
              data-placement={placement ?? undefined}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {search.isFetching ? (
                <div className="simsm-medicombo__row simsm-medicombo__row--meta" role="status">
                  {t('medicine_combobox.loading')}
                </div>
              ) : null}
              {!search.isFetching && search.isError ? (
                // A search that failed is not a search that found nothing. This
                // row used to say "no results for «…»" whenever the request was
                // refused, so an unreachable catalogue read as a medicine that
                // does not exist — on the screen where a stock report is filled in.
                <div className="simsm-medicombo__row simsm-medicombo__row--meta" role="status">
                  {t('common.load_error')}
                </div>
              ) : null}
              {!search.isFetching && !search.isError && hits.length === 0 ? (
                <div className="simsm-medicombo__row simsm-medicombo__row--meta" role="status">
                  {t('medicine_combobox.no_results', { query: debounced })}
                </div>
              ) : null}
              {/* The listbox exists only while it holds options: an empty one is a
                  role whose required children are missing, and the three messages
                  above are status text, not choices. */}
              {hits.length > 0 ? (
                <div
                  ref={listboxRef}
                  id={combobox.listboxId}
                  role="listbox"
                  // In the tab order, and named, because the panel around it is a
                  // scroll container: a scrollable region whose only contents are
                  // `tabindex="-1"` options is unreachable by keyboard in Safari,
                  // and axe reports it as such (`scrollable-region-focusable`). The
                  // options keep roving focus; this is the container that answers
                  // for them, and arrow keys and Escape work on it.
                  tabIndex={0}
                  className="simsm-medicombo__list"
                  aria-label={t('medicine_combobox.results_aria_label')}
                  onKeyDown={combobox.onListboxKeyDown}
                >
                  {hits.map((h, index) => {
                    // caiet UC50 step 4: the result carries the authorization
                    // expiry for the authorized codes, and only for those. An
                    // authorization that has already lapsed is not an ordinary
                    // date — UC20-10 keeps the code reportable through a grace
                    // period after it, so the row does the arithmetic and says
                    // which side of today the date falls on, in the verb rather
                    // than only in the colour.
                    const members = groupByMedicine ? (groups[index]?.members ?? [h]) : [h];
                    const authorization = describeAuthorization(h, today);
                    // Grouped: one line per code, because each code of a unique
                    // medicine carries its own authorization (TC-UC92-02-M01: the
                    // result shows the expiry of the authorized codes).
                    const authorizationLines = groupByMedicine
                      ? members.flatMap((member) => {
                          const note = describeAuthorization(member, today);
                          return note ? [{ code: member.code, note }] : [];
                        })
                      : [];
                    return (
                      <div
                        key={h.code}
                        id={combobox.optionId(index)}
                        role="option"
                        tabIndex={-1}
                        aria-selected={combobox.activeIndex === index}
                        className="simsm-medicombo__row"
                        onFocus={() => combobox.onOptionFocus(index)}
                        onClick={() => combobox.selectOption(index)}
                      >
                        <span className="simsm-medicombo__main">
                          <span className="simsm-medicombo__title">{formatMedicineLabel(h)}</span>
                          {groupByMedicine ? (
                            <span className="simsm-medicombo__meta">
                              <span className="simsm-medicombo__code">
                                {(groups[index]?.codes ?? [h.code]).join(', ')}
                              </span>
                              {h.atcCode ? ` · ${h.atcCode}` : null}
                            </span>
                          ) : (
                            <span className="simsm-medicombo__meta">
                              <span className="simsm-medicombo__code">{h.code}</span>
                              {h.atcCode ? ` · ${h.atcCode}` : null}
                              {/* The certificate holder is the one fragment that carries a
                                label. Unlabelled, a second organisation on this line
                                leaves the reader unable to tell it from the producer;
                                the wireframe's own row labels its metadata for exactly
                                that reason (spec 024). */}
                              {medicineOrigin(h)
                                .map(
                                  (part) =>
                                    ` · ${part.labelKey ? `${t(part.labelKey)}: ` : ''}${part.value}`,
                                )
                                .join('')}
                            </span>
                          )}
                          {groupByMedicine
                            ? authorizationLines.map(({ code, note }) => (
                                <span
                                  key={code}
                                  className={`simsm-medicombo__authorization${
                                    note.expired ? ' simsm-medicombo__authorization--expired' : ''
                                  }`}
                                >
                                  {code} · {t(note.key, { date: formatDate(note.iso) })}
                                </span>
                              ))
                            : null}
                          {authorization && !groupByMedicine ? (
                            <span
                              className={`simsm-medicombo__authorization${
                                authorization.expired
                                  ? ' simsm-medicombo__authorization--expired'
                                  : ''
                              }`}
                            >
                              {t(authorization.key, { date: formatDate(authorization.iso) })}
                            </span>
                          ) : null}
                        </span>
                        {members.some((member) => member.authorized) ? null : (
                          <MudTag
                            variant="info"
                            semantic="muted"
                            type="subtle"
                            size="sm"
                            label={t('medicine_combobox.unauthorized')}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
